"use client";
import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  IconButton,
  Menu,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  useMediaQuery,
  useTheme,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Switch,
} from "@mui/material";
import {
  Filter,
  Download,
  Save,
  X,
  Search,
  Tally3 as Columns3,
  RefreshCw,
  Edit,
  Trash2,
} from "lucide-react";
import { Eye } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";

import toast from "react-hot-toast";
import useAuthStore from "../../Zustand/Store/useAuthStore";
import {
  saveLayoutToConfig,
  resetLayoutToDefault,
  getUserConfig,
  getDefaultConfig,
  getTableConfig,
} from "../../Configurations/TableDataConfig";
import PushPinIcon from "@mui/icons-material/PushPin";
import { FilterBuilder } from "../filters";
import {
  OPERATORS,
  operatorRequiresMultipleValues,
  operatorRequiresValue,
} from "../filters/operators";
import ActiveFiltersBar from "./ActiveFiltersBar";
import SavedViewsBar from "./SavedViewsBar";
import Toolbar from "./Toolbar";
import TablePagination from "./TablePagination";
import ListRecordCard from "./ListRecordCard";
import { MAIN_URL } from "../../Configurations/Urls";
import {
  StatusPill,
  PriorityPill,
  NameWithAvatar,
  isStatusColumnKey,
  isPriorityColumnKey,
  isNameAvatarColumn,
  isReportTableName,
  formatCellDisplay,
  wrapPlainListCell,
} from "./listCellHelpers";
// Constants to avoid recreating objects
const DEFAULT_ROWS_PER_PAGE = 10;

/** EMS Certificates-style empty list body: slim blank head bar + centered message. */
function EmptyTableState({ asCard = true }) {
  const body = (
    <>
      <div className="ems-table-empty-head" aria-hidden />
      <div className="ems-empty ems-empty-simple">No records found.</div>
    </>
  );
  if (!asCard) return body;
  return <div className="ems-card ems-table-card">{body}</div>;
}

/** Row shape may include __is_active_for_toggle (Users grid) or raw is_active. */
function getRowActiveForToggle(row) {
  if (!row) return false;
  if (row.__is_active_for_toggle !== undefined) {
    return Boolean(row.__is_active_for_toggle);
  }
  const v = row.is_active;
  if (v === true || v === 1 || v === "1") return true;
  if (v === "Active") return true;
  return false;
}

function isToggleActiveDisabled(row) {
  return Boolean(row?.__toggle_active_disabled) && getRowActiveForToggle(row);
}

function getToggleActiveTooltip(row) {
  if (isToggleActiveDisabled(row)) {
    return (
      row?.__toggle_active_disabled_reason ||
      "Cannot deactivate this user"
    );
  }
  return getRowActiveForToggle(row) ? "Deactivate" : "Activate";
}

const formatLabel = (label) => {
  return String(label ?? "").replace(/_/g, " ");
};

const resolveColumnWidth = (width, fallback = 150) => {
  const numeric = Number(width);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : fallback;
};

const MIN_COL_WIDTH = 50;
const ACTIONS_COL_KEY = "__actions";
const DEFAULT_ACTIONS_COL_WIDTH = 140;
const CHECKBOX_COL_WIDTH = 50;

const toColPercent = (px, total) =>
  `${((Number(px) / Math.max(total, 1)) * 100).toFixed(4)}%`;

const getNextVisibleColumn = (columns, key) => {
  const visible = (columns || []).filter((col) => col.visible);
  const index = visible.findIndex((col) => col.key === key);
  return index >= 0 ? visible[index + 1] || null : null;
};

const resizeAdjacentColumns = (
  columns,
  currentKey,
  nextKey,
  startWidth,
  startNextWidth,
  deltaX
) => {
  if (nextKey) {
    let newCurrent = Math.max(MIN_COL_WIDTH, startWidth + deltaX);
    let applied = newCurrent - startWidth;
    let newNext = startNextWidth - applied;
    if (newNext < MIN_COL_WIDTH) {
      newNext = MIN_COL_WIDTH;
      applied = startNextWidth - MIN_COL_WIDTH;
      newCurrent = startWidth + applied;
    }
    return columns.map((col) => {
      if (col.key === currentKey) return { ...col, width: newCurrent };
      if (col.key === nextKey) return { ...col, width: newNext };
      return col;
    });
  }

  return columns.map((col) =>
    col.key === currentKey
      ? { ...col, width: Math.max(MIN_COL_WIDTH, startWidth + deltaX) }
      : col
  );
};

const matchConfigColumn = (col, configCol) => {
  const colKey = col.key || col.field;
  const configKey = configCol.key || configCol.field;
  return Boolean(colKey) && colKey === configKey;
};

const buildConfiguredColumns = (tableConfig, configToUse) =>
  configToUse.columns
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map((configCol) => {
      const defaultCol = tableConfig.default_config.columns.find((col) =>
        matchConfigColumn(col, configCol)
      );
      const key = configCol.key || configCol.field;
      return {
        key,
        field: configCol.field || configCol.key || key,
        label: defaultCol?.label || configCol.label || key,
        width: resolveColumnWidth(
          configCol.width,
          resolveColumnWidth(defaultCol?.width)
        ),
        visible: configCol.visible !== undefined ? configCol.visible : true,
        sortable:
          defaultCol?.sortable !== undefined ? defaultCol.sortable : true,
        filterable:
          defaultCol?.filterable !== undefined ? defaultCol.filterable : true,
        pinned: configCol.pinned === "left" || configCol.pinned === true,
        order: configCol.order ?? defaultCol?.order ?? 0,
        dataType: defaultCol?.dataType || configCol.dataType,
        mandatory: defaultCol?.mandatory || configCol.mandatory,
      };
    });

const getColumnWidthSx = () => ({
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
});

// Helper function to construct proper image URL
const getImageUrl = (imagePath) => {
  if (!imagePath) return "https://avatar.iran.liara.run/public/47";

  // If it's already a full URL (starts with http), return as is
  if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) {
    return imagePath;
  }

  // If it starts with /storage/, construct full URL with backend
  if (imagePath.startsWith("/storage/app/public")) {
    return `${MAIN_URL}${imagePath}`;
  }

  // If it's just a filename or relative path, construct full URL
  if (imagePath.startsWith("storage/")) {
    return `${MAIN_URL}/${imagePath}`;
  }

  // If it's just a filename, assume it's in storage/employees/
  return `${MAIN_URL}/storage/app/public/employees${imagePath}`;
};

function isLoggedInUserRow(row, userData) {
  if (!row || !userData) return false;
  const orgIds = [
    userData.organizationuser?.organization_user_id,
    userData.organization_user?.organization_user_id,
    userData.organization_user_id,
    ...(Array.isArray(userData.metadata)
      ? userData.metadata.map((item) => item?.organization_user_id)
      : []),
  ].filter((value) => value != null && value !== "");
  const appIds = [
    userData.application_user_id,
    userData.user_id,
    userData.applicationuser?.application_user_id,
    Array.isArray(userData.applicationuser)
      ? userData.applicationuser[0]?.application_user_id
      : null,
  ].filter((value) => value != null && value !== "");
  const rowOrg =
    row.organization_user_id ??
    row.organization_user?.organization_user_id ??
    row.id;
  const rowApp =
    row.application_user_id ??
    row.application_user?.application_user_id;
  if (rowOrg != null && orgIds.length > 0) {
    return orgIds.some((id) => String(id) === String(rowOrg));
  }
  if (rowOrg != null && orgIds.length === 0 && rowApp != null) {
    return appIds.some((id) => String(id) === String(rowApp));
  }
  if (rowOrg == null && rowApp != null && appIds.some((id) => String(id) === String(rowApp))) {
    return true;
  }
  return false;
}

function isUsersListTable(tableName) {
  const name = String(tableName || "").toLowerCase();
  return name === "users" || name === "organization_users";
}

function UsersDeleteButton({ row, userData, tableName, onDelete, sx, stopPropagation = false }) {
  const locked = isUsersListTable(tableName) && isLoggedInUserRow(row, userData);
  return (
    <Tooltip title={locked ? "You cannot delete your own account" : "Delete"}>
      <span>
        <IconButton
          size="small"
          disabled={locked}
          onClick={(e) => {
            if (stopPropagation) e.stopPropagation();
            if (!locked) onDelete(row);
          }}
          sx={sx}
        >
          <Trash2 size={16} />
        </IconButton>
      </span>
    </Tooltip>
  );
}

const Customisetable = ({
  Route,
  configss,
  onFilterChange,
  onSortChange,
  onPageChange,
  DeleteFunc,
  onclickRow,
  EditFunc,
  configuration,
  tableName,
  recordKey,
  recordLabel,
  mainKey,
  title,
  linkType,
  mainData,
  showActions,
  CardColoumn = [],
  hideToolbar,
  onSaveLayout,
  handleShow,
  onResetLayout,
  loadedBackendConfig,
  paginationData,
  filterModule, // Optional: module name for filter builder (defaults to tableName)
  /** Optional: (row) => ReactNode — extra row action buttons */
  additionalActions,
  /** (row, isActiveNext: boolean) => void | Promise — e.g. Users active/inactive toggle */
  toggleActiveFn,
  /** List page title — shown on Personalize View to match the list heading. */
  heading: headingProp,
  /** When true, delete dialog offers restore employee vs delete exit record only. */
  deleteRestoreOption = false,
  /** Optional: parent can refetch list data after delete (no full page reload). */
  onAfterDelete,
  /** When false, name columns render as plain text (used for reports). Default: auto from tableName. */
  enableNameAvatar,
  setPaginationData,
  toolbarExtras,
}) => {
  const { userData } = useAuthStore();
  const showNameAvatar =
    enableNameAvatar !== false && !isReportTableName(tableName);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const isSmallScreen = isMobile || isTablet;

  const listHeading =
    headingProp ||
    configuration?.[0]?.heading ||
    title ||
    "";

  const [showRowCheckboxes, setShowRowCheckboxes] = useState(false);

  const navigate = useNavigate();

  const [view, setView] = useState("table");

  const [columns, setColumns] = useState([]);
  const [originalColumnOrder, setOriginalColumnOrder] = useState([]);
  const [data, setData] = useState([]);
  const deletedIdsRef = useRef(new Set());

  const applyDeletedFilter = useCallback(
    (rows) => {
      if (!Array.isArray(rows) || deletedIdsRef.current.size === 0) {
        return rows;
      }
      return rows.filter((item) => !deletedIdsRef.current.has(item?.[mainKey]));
    },
    [mainKey]
  );
  const [sortConfig, setSortConfig] = useState(
    configuration?.[0]?.default_config.sortConfig || []
  );
  const [filters, setFilters] = useState({});
  const [pendingFilters, setPendingFilters] = useState({});
  const [pendingFilterBuilderFilters, setPendingFilterBuilderFilters] =
    useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [columnManagerOpen, setColumnManagerOpen] = useState(false);
  const [draggedColumnIndex, setDraggedColumnIndex] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [currentPage, setCurrentPage] = useState(1);
  const [resizingColumnKey, setResizingColumnKey] = useState(null);
  const [actionsColWidth, setActionsColWidth] = useState(DEFAULT_ACTIONS_COL_WIDTH);
  const initialMouseX = useRef(0);
  const initialColumnWidth = useRef(0);
  const initialNextColumnKey = useRef(null);
  const initialNextColumnWidth = useRef(0);
  const [pendingColumns, setPendingColumns] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const hasInitializedFromURL = useRef(false);

  const [backendPagination, setBackendPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
    totalPages: 1,
    hasNext: false,
    hasPrev: false,
  });

  // Rows deleted locally since the last server response. Keeps the pagination
  // counters correct instantly, until the parent refetches fresh data.
  const [deleteAdjustment, setDeleteAdjustment] = useState(0);

  useEffect(() => {
    setDeleteAdjustment(0);
  }, [paginationData]);

  const [selectedRows, setSelectedRows] = useState([]);
  const [lockedFilters, setLockedFilters] = useState({});
  const [viewSavedTrigger, setViewSavedTrigger] = useState(0);

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const allIds = paginatedData.map((row) => row[mainKey]);
      setSelectedRows(allIds);
    } else {
      setSelectedRows([]);
    }
  };

  const handleSelectRow = (id) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]
    );
  };

  useEffect(() => {
    if (hasInitializedFromURL.current) return;

    const pageParam = Number.parseInt(searchParams.get("page")) || 1;
    const pageSizeParam =
      Number.parseInt(searchParams.get("pageSize")) || DEFAULT_ROWS_PER_PAGE;

    // Load search query from URL
    const searchParam = searchParams.get("search") || "";

    // Load filters from URL params
    const urlFilters = {};
    const urlOperators = {};
    searchParams.forEach((value, key) => {
      if (key.startsWith("filter_") && !key.endsWith("_operator")) {
        const filterKey = key.replace("filter_", "");
        // Check if operator doesn't require value
        const operatorKey = `filter_${filterKey}_operator`;
        const operator = searchParams.get(operatorKey);
        const requiresValue = operator ? operatorRequiresValue(operator) : true;

        // Include filter if value exists OR if operator doesn't require value
        if (value || !requiresValue) {
          urlFilters[filterKey] = value || "";
          if (operator) {
            urlOperators[filterKey] = operator;
          }
        }
      }
    });

    setCurrentPage(pageParam);
    setRowsPerPage(pageSizeParam);
    setFilters(urlFilters);
    setSearchQuery(searchParam);
    setPendingFilters(urlFilters);

    hasInitializedFromURL.current = true;
  }, []);

  useEffect(() => {
    if (paginationData) {
      setBackendPagination(paginationData);

      const urlPage = Number.parseInt(searchParams.get("page")) || 1;
      const urlPageSize =
        Number.parseInt(searchParams.get("pageSize")) || DEFAULT_ROWS_PER_PAGE;

      // Backend may return limit instead of pageSize
      const backendPageSize =
        paginationData.pageSize || paginationData.limit || urlPageSize;
      const backendPage = paginationData.page || urlPage;

      // Always trust URL params for page (user-initiated changes)
      // Only sync pageSize from backend if it matches URL
      setCurrentPage(urlPage);

      // For pageSize, prefer backend if it matches URL, otherwise use URL
      if (backendPageSize === urlPageSize || !paginationData.pageSize) {
        setRowsPerPage(urlPageSize);
      } else {
        setRowsPerPage(backendPageSize);
      }
    }
  }, [paginationData, searchParams]);

  // Helper function to update URL
  const updateURL = useCallback(
    (updates) => {
      // Functional update — React Router's setSearchParams uses the render-time
      // searchParams when handed a plain object after another update in the same
      // click (e.g. parent onFilterChange), which would wipe filter_* params.
      setSearchParams((prev) => {
        const newSearchParams = new URLSearchParams(prev);

        // Update filters
        if (updates.filters !== undefined) {
          // Remove all existing filter params (including operators and logics)
          Array.from(newSearchParams.keys()).forEach((key) => {
            if (key.startsWith("filter_")) {
              newSearchParams.delete(key);
            }
          });

          // Add new filter params
          Object.entries(updates.filters).forEach(([key, value]) => {
            // Check if operator requires a value
            const operator = updates.filterOperators?.[key];
            const requiresValue = operator
              ? operatorRequiresValue(operator)
              : true;

            // For operators that don't require values, allow empty string
            // For operators that require values, only add if value is valid
            if (
              !requiresValue ||
              (value !== null && value !== undefined && value !== "")
            ) {
              const stringValue = Array.isArray(value)
                ? value.map((v) => String(v)).join(",")
                : value === null || value === undefined
                  ? ""
                  : String(value);
              // For operators that don't require values, use empty string as placeholder
              if (!requiresValue || stringValue.trim() !== "") {
                newSearchParams.set(
                  `filter_${key}`,
                  requiresValue ? stringValue : ""
                );
              }
            }
          });

          // Add filter operators if provided
          if (updates.filterOperators !== undefined) {
            Object.entries(updates.filterOperators).forEach(([key, operator]) => {
              if (operator) {
                // Add operator if filter exists OR if operator doesn't require value
                const requiresValue = operatorRequiresValue(operator);
                const hasFilter =
                  updates.filters && updates.filters[key] !== undefined;
                if (hasFilter || !requiresValue) {
                  newSearchParams.set(`filter_${key}_operator`, operator);
                  // If operator doesn't require value, also add empty filter value
                  if (
                    !requiresValue &&
                    (!updates.filters || !updates.filters[key])
                  ) {
                    newSearchParams.set(`filter_${key}`, "");
                  }
                }
              }
            });
          }
        }

        // Update sort
        if (updates.sortConfig !== undefined && updates.sortConfig.length > 0) {
          newSearchParams.set("sortBy", updates.sortConfig[0].key);
          newSearchParams.set("sortOrder", updates.sortConfig[0].direction);
        }

        // Update search
        if (updates.searchQuery !== undefined) {
          if (updates.searchQuery.trim() !== "") {
            newSearchParams.set("search", updates.searchQuery);
          } else {
            newSearchParams.delete("search");
          }
        }

        // Update page — always keep page in the URL so list fetches stay in sync
        if (updates.currentPage !== undefined) {
          newSearchParams.set(
            "page",
            String(Math.max(1, Number(updates.currentPage) || 1))
          );
        }

        // Update rows per page — always keep pageSize in the URL
        if (updates.rowsPerPage !== undefined) {
          newSearchParams.set(
            "pageSize",
            String(
              Math.max(1, Number(updates.rowsPerPage) || DEFAULT_ROWS_PER_PAGE)
            )
          );
        }

        return newSearchParams;
      }, { replace: true });
    },
    [setSearchParams]
  );

  // Cache for nested values to avoid recalculating
  const valueCache = useRef(new Map());

  const colors = useMemo(
    () => ({
      primary: theme.palette.primary.main,
      secondary: theme.palette.secondary.main,
      success: theme.palette.success.main,
      warning: theme.palette.warning.main,
      error: theme.palette.error.main,
      info: theme.palette.info.main,
      background: theme.palette.background.default,
      surface: theme.palette.background.paper,
      text: {
        primary: theme.palette.text.primary,
        secondary: theme.palette.text.secondary,
      },
      grey: {
        50:
          theme.palette.mode === "dark"
            ? theme.palette.grey[900]
            : theme.palette.grey[50],
        100:
          theme.palette.mode === "dark"
            ? theme.palette.grey[800]
            : theme.palette.grey[100],
        200:
          theme.palette.mode === "dark"
            ? theme.palette.grey[700]
            : theme.palette.grey[200],
        300:
          theme.palette.mode === "dark"
            ? theme.palette.grey[600]
            : theme.palette.grey[300],
        400:
          theme.palette.mode === "dark"
            ? theme.palette.grey[500]
            : theme.palette.grey[400],
        500:
          theme.palette.mode === "dark"
            ? theme.palette.grey[400]
            : theme.palette.grey[500],
        600:
          theme.palette.mode === "dark"
            ? theme.palette.grey[300]
            : theme.palette.grey[600],
        700:
          theme.palette.mode === "dark"
            ? theme.palette.grey[200]
            : theme.palette.grey[700],
        800:
          theme.palette.mode === "dark"
            ? theme.palette.grey[100]
            : theme.palette.grey[800],
        900:
          theme.palette.mode === "dark"
            ? theme.palette.grey[50]
            : theme.palette.grey[900],
      },
      departments: {
        Design: "#e91e63",
        HR: "#ff9800",
        Finance: "#4caf50",
        Marketing: "#9c27b0",
        Engineering: "#2196f3",
        Sales: "#ff5722",
        Support: "#795548",
        default: theme.palette.text.secondary,
      },
    }),
    [theme]
  );

  useEffect(() => {
    if (configuration?.[0]?.default_config?.columns?.length && tableName) {
      const tableConfig = getTableConfig(configuration, tableName);
      if (!tableConfig) return;

      const userConfig = getUserConfig(configuration, tableName);
      const configToUse =
        userConfig || getDefaultConfig(configuration, tableName);
      if (!configToUse?.columns?.length) return;

      const configuredColumns = buildConfiguredColumns(
        tableConfig,
        configToUse
      );
      setColumns(configuredColumns);
      setOriginalColumnOrder(configuredColumns);

      if (configToUse.sortConfig) {
        setSortConfig(configToUse.sortConfig);
      }
      if (configToUse.rowsPerPage) {
        setRowsPerPage(configToUse.rowsPerPage);
      }
      return;
    }

    if (loadedBackendConfig?.columns?.length) {
      const mandatoryColumnsArray =
        loadedBackendConfig.defaults?.mandatoryColumns || [];

      const backendColumns = loadedBackendConfig.columns.map((col) => ({
        ...col,
        key: col.key || col.field,
        field: col.field || col.key,
        width: resolveColumnWidth(col.width),
        mandatory:
          mandatoryColumnsArray.includes(col.key) ||
          mandatoryColumnsArray.includes(col.field),
      }));
      setColumns(backendColumns);
      setOriginalColumnOrder(backendColumns);

      if (loadedBackendConfig.sortConfig) {
        setSortConfig(loadedBackendConfig.sortConfig);
      }

      if (loadedBackendConfig.rowsPerPage) {
        setRowsPerPage(loadedBackendConfig.rowsPerPage);
      }
    }
  }, [loadedBackendConfig, configuration, tableName]);

  const getNestedValue = useCallback((obj, path) => {
    const cacheKey = `${obj?.[mainKey] || JSON.stringify(obj)}_${path}`;
    if (valueCache.current.has(cacheKey)) {
      return valueCache.current.get(cacheKey);
    }
    const value = path?.split(".").reduce((acc, part) => acc && acc[part], obj);
    valueCache.current.set(cacheKey, value);
    return value;
  }, []);

  const actionBtnSx = useMemo(
    () => ({
      color: "var(--ems-muted)",
      "&:hover": {
        color: "var(--ems-primary)",
        backgroundColor: "var(--ems-primary-tint-2)",
      },
    }),
    []
  );

  const renderDataCellContent = useCallback(
    (row, col) => {
      const raw = getNestedValue(row, col.key);
      const linkField = String(linkType || "").toLowerCase();
      const isLinkCol =
        Boolean(linkField) &&
        (col.key?.toLowerCase() === linkField ||
          col.label?.toLowerCase() === linkField);

      if (isPriorityColumnKey(col.key)) {
        return <PriorityPill value={raw} />;
      }

      if (isStatusColumnKey(col.key)) {
        return <StatusPill value={raw} />;
      }

      if (isNameAvatarColumn(col.key, tableName, showNameAvatar)) {
        const name = String(raw ?? "").trim() || "—";
        return (
          <NameWithAvatar
            row={row}
            name={name}
            getNestedValue={getNestedValue}
            clickable={isLinkCol && Boolean(onclickRow)}
            onClick={
              isLinkCol && onclickRow
                ? () => {
                    onclickRow(row);
                  }
                : undefined
            }
          />
        );
      }

      const display = formatCellDisplay(raw);

      if (isLinkCol && onclickRow) {
        return (
          <Box
            component="span"
            className="ems-list-oneline"
            onClick={(e) => {
              e.stopPropagation();
              onclickRow(row);
            }}
            sx={{
              cursor: "pointer",
              color: "var(--ems-primary)",
              fontWeight: 650,
              textDecoration: "none",
            }}
            title={title || (typeof display === "string" && display !== "—" ? display : undefined)}
          >
            {display}
          </Box>
        );
      }

      return wrapPlainListCell(display);
    },
    [getNestedValue, tableName, linkType, onclickRow, title, showNameAvatar]
  );

  const loadDataFromConfiguration = useCallback(
    async (tableConfigName) => {
      setLoading(true);
      setData([]);
      setColumns([]);
      // setFilters({});
      // setPendingFilters({});
      // Server-side pagination: keep page from URL; client-side: reset to 1
      if (paginationData?.totalPages) {
        const urlPage = Number.parseInt(searchParams.get("page"), 10) || 1;
        setCurrentPage(urlPage);
      } else {
        setCurrentPage(1);
      }

      try {
        const result = mainData;

        if (!Array.isArray(result) || result.length === 0) {
          console.warn(
            "API did not return an array of objects or returned empty data."
          );

          const tableConfig = getTableConfig(configuration, tableConfigName);
          if (tableConfig?.default_config?.columns?.length) {
            const userConfig = getUserConfig(configuration, tableConfigName);
            const configToUse =
              userConfig || getDefaultConfig(configuration, tableConfigName);
            const configuredColumns = buildConfiguredColumns(
              tableConfig,
              configToUse
            );

            setColumns(configuredColumns);
            setOriginalColumnOrder(configuredColumns);
          } else if (loadedBackendConfig?.columns?.length) {
            const mandatoryColumnsArray =
              loadedBackendConfig.defaults?.mandatoryColumns || [];
            setColumns(
              loadedBackendConfig.columns.map((col) => ({
                ...col,
                key: col.key || col.field,
                field: col.field || col.key,
                mandatory:
                  mandatoryColumnsArray.includes(col.key) ||
                  mandatoryColumnsArray.includes(col.field),
              }))
            );
          }
          setData([]);
          return;
        }

        valueCache.current.clear();

        const tableConfig = getTableConfig(configuration, tableConfigName);

        if (tableConfig?.default_config?.columns?.length) {
          const userConfig = getUserConfig(configuration, tableConfigName);
          const configToUse =
            userConfig || getDefaultConfig(configuration, tableConfigName);
          const configuredColumns = buildConfiguredColumns(
            tableConfig,
            configToUse
          );

          setData(applyDeletedFilter(result));
          setColumns(configuredColumns);
          setOriginalColumnOrder(configuredColumns);

          if (configToUse.sortConfig) {
            setSortConfig(configToUse.sortConfig);
          }
          if (configToUse.rowsPerPage) {
            setRowsPerPage(configToUse.rowsPerPage);
          }
          if (configToUse.filters) {
            const urlAlreadyHasFilters = Array.from(searchParams.keys()).some(
              (key) => key.startsWith("filter_")
            );
            if (!urlAlreadyHasFilters) {
              setFilters(configToUse.filters);
            }
          }
          return;
        }

        if (loadedBackendConfig && loadedBackendConfig.columns) {
          const mandatoryColumnsArray =
            loadedBackendConfig.defaults?.mandatoryColumns || [];

          const backendColumns = loadedBackendConfig.columns.map((col) => ({
            ...col,
            key: col.key || col.field,
            field: col.field || col.key,
            width: resolveColumnWidth(col.width),
            mandatory:
              mandatoryColumnsArray.includes(col.key) ||
              mandatoryColumnsArray.includes(col.field),
          }));

          setData(applyDeletedFilter(result));
          setColumns(backendColumns);
          setOriginalColumnOrder(backendColumns);

          if (loadedBackendConfig.sortConfig) {
            setSortConfig(loadedBackendConfig.sortConfig);
          }
          if (loadedBackendConfig.rowsPerPage) {
            setRowsPerPage(loadedBackendConfig.rowsPerPage);
          }
          if (loadedBackendConfig.filters) {
            const urlAlreadyHasFilters = Array.from(searchParams.keys()).some(
              (key) => key.startsWith("filter_")
            );
            if (!urlAlreadyHasFilters) {
            // Handle filters - check if in FilterBuilder format (array) or simple format (object)
            // Note: We inline the logic here to avoid initialization order issues
            const configFilters = loadedBackendConfig.filters;

            if (Array.isArray(configFilters) && configFilters.length > 0) {
              setPendingFilterBuilderFilters(configFilters);
            } else if (
              typeof configFilters === "object" &&
              !Array.isArray(configFilters)
            ) {
              setFilters(configFilters);
              setPendingFilters(configFilters);
            }
            }
          }
          return;
        }

        if (!tableConfig) {
          console.warn(`No configuration found for table: ${tableConfigName}`);
          setData(applyDeletedFilter(result));
          return;
        }

        console.warn(
          `Table ${tableConfigName} has no column configuration; using data keys.`
        );
        setData(applyDeletedFilter(result));
      } catch (error) {
        console.error("Failed to load data with config:", error);
        setSnackbar({
          open: true,
          message: `Failed to load data: ${error.message}`,
          severity: "error",
        });
      } finally {
        setLoading(false);
      }
    },
    [getNestedValue, tableName, mainData, loadedBackendConfig, configuration, paginationData, searchParams, applyDeletedFilter]
  );

  useEffect(() => {
    if (tableName) {
      loadDataFromConfiguration(tableName);
    }
  }, [loadDataFromConfiguration, tableName, loadedBackendConfig, configuration]);

  const parseDateTimeValue = (value) => {
    if (!value || typeof value !== "string") return null;

    const timestamp = Date.parse(value);
    if (!Number.isNaN(timestamp)) {
      return timestamp;
    }

    const dateTimeRegex =
      /^(\d{2})-(\d{2})-(\d{4})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/;
    const match = value.match(dateTimeRegex);

    if (!match) return null;

    const [, dd, mm, yyyy, hh, min, ss] = match;
    const day = Number.parseInt(dd, 10);
    const month = Number.parseInt(mm, 10) - 1;
    const year = Number.parseInt(yyyy, 10);
    const hours = Number.parseInt(hh, 10);
    const minutes = Number.parseInt(min, 10);
    const seconds = Number.parseInt(ss || "0", 10);

    const parsed = new Date(year, month, day, hours, minutes, seconds).getTime();
    return Number.isNaN(parsed) ? null : parsed;
  };

  const sortedData = useMemo(() => {
    const currentSort = sortConfig[0];
    if (!currentSort || !currentSort.key) return data;

    return [...data].sort((a, b) => {
      const aVal = getNestedValue(a, currentSort.key);
      const bVal = getNestedValue(b, currentSort.key);

      const parsedDateA = parseDateTimeValue(aVal);
      const parsedDateB = parseDateTimeValue(bVal);

      if (parsedDateA !== null && parsedDateB !== null) {
        return currentSort.direction === "asc"
          ? parsedDateA - parsedDateB
          : parsedDateB - parsedDateA;
      }

      const timeRegex =
        /^([0-1]?[0-9]|2[0-3]):[0-5][0-9](:[0-5][0-9])?(\s?(AM|PM))?$/i;
      if (
        typeof aVal === "string" &&
        typeof bVal === "string" &&
        timeRegex.test(aVal) &&
        timeRegex.test(bVal)
      ) {
        const parseTime = (timeStr) => {
          const [time, period] = timeStr.trim().split(" ");
          let [hours, minutes, seconds] = time.split(":").map(Number);
          if (isNaN(seconds)) seconds = 0;
          if (period?.toUpperCase() === "PM" && hours < 12) hours += 12;
          if (period?.toUpperCase() === "AM" && hours === 12) hours = 0;
          return hours * 3600 + minutes * 60 + seconds;
        };

        const timeA = parseTime(aVal);
        const timeB = parseTime(bVal);
        return currentSort.direction === "asc" ? timeA - timeB : timeB - timeA;
      }

      if (typeof aVal === "string" && typeof bVal === "string") {
        return currentSort.direction === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      if (typeof aVal === "number" && typeof bVal === "number") {
        return currentSort.direction === "asc" ? aVal - bVal : bVal - aVal;
      }

      if (aVal < bVal) return currentSort.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return currentSort.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [data, sortConfig, getNestedValue]);

  const filteredData = useMemo(() => {
    let result = sortedData;

    if (searchQuery.trim()) {
      const query = searchQuery?.toLowerCase().trim();
      result = result.filter((item) => {
        return (
          columns.some((col) => {
            const value = getNestedValue(item, col.key);
            if (value === null || value === undefined) return false;

            const stringValue = String(value)?.toLowerCase();
            const formattedLabel = formatLabel(col.key)?.toLowerCase();

            return (
              stringValue.includes(query) || formattedLabel.includes(query)
            );
          }) ||
          Object.values(item).some((value) => {
            if (value === null || value === undefined) return false;
            return String(value)?.toLowerCase().includes(query);
          })
        );
      });
    }

    Object.entries(filters).forEach(([key, value]) => {
      // Skip null/undefined values
      if (value === null || value === undefined) return;

      // Handle string values
      if (typeof value === "string") {
        if (value.trim() === "") return;

        const filterValue = value.toLowerCase().trim();
        result = result.filter((item) => {
          const itemValue = getNestedValue(item, key);
          if (itemValue === null || itemValue === undefined) return false;

          const itemString = String(itemValue)?.toLowerCase();
          const filterWords = filterValue
            ?.split(/\s+/)
            .filter((word) => word.length > 0);

          return filterWords.every((word) => itemString.includes(word));
        });
      }
      // Handle array values (for BETWEEN, IN, NOT_IN operators)
      else if (Array.isArray(value)) {
        // Skip empty arrays or arrays with all empty values
        const hasValidValues = value.some(
          (v) =>
            v !== null &&
            v !== undefined &&
            v !== "" &&
            !(typeof v === "string" && v.trim() === "")
        );
        if (!hasValidValues) return;

        // Note: Array-based filtering handled by backend/URL params
        // This section is for basic client-side filtering only
        // Advanced operators like BETWEEN are typically handled server-side
      }
    });

    return result;
  }, [sortedData, filters, searchQuery, columns]);

  useEffect(() => {
    // Only validate page for client-side pagination
    // For server-side pagination, trust the backend response
    if (paginationData?.totalPages) {
      // Server-side pagination - backend handles validation
      return;
    }

    // Client-side pagination validation
    const totalPages = Math.max(
      1,
      Math.ceil(filteredData.length / rowsPerPage)
    );
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
      updateURL({ currentPage: totalPages });
    } else if (filteredData.length === 0 && currentPage !== 1) {
      setCurrentPage(1);
      updateURL({ currentPage: 1 });
    }
  }, [
    filteredData.length,
    rowsPerPage,
    currentPage,
    updateURL,
    paginationData,
  ]);

  // Server-side pagination figures corrected for locally deleted rows.
  const effectivePaginationData = useMemo(() => {
    if (!paginationData || !deleteAdjustment) {
      return paginationData;
    }
    const pageSize =
      paginationData.pageSize || paginationData.limit || rowsPerPage || 10;
    const total = Math.max(0, (paginationData.total ?? 0) - deleteAdjustment);
    const newTotalPages = Math.max(1, Math.ceil(total / pageSize));
    return {
      ...paginationData,
      total,
      totalPages: newTotalPages,
      hasNext: currentPage < newTotalPages,
      hasPrev: currentPage > 1,
    };
  }, [paginationData, deleteAdjustment, rowsPerPage, currentPage]);

  const totalPages = useMemo(() => {
    if (effectivePaginationData?.totalPages) {
      return effectivePaginationData.totalPages;
    }
    const pages = Math.max(1, Math.ceil(filteredData.length / rowsPerPage));
    return pages;
  }, [filteredData.length, rowsPerPage, effectivePaginationData]);

  const hasNext = effectivePaginationData?.hasNext ?? currentPage < totalPages;
  const hasPrev = effectivePaginationData?.hasPrev ?? currentPage > 1;

  const paginatedData = useMemo(() => {
    if (paginationData?.totalPages) {
      return data;
    }
    // Otherwise, paginate client-side
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return filteredData.slice(startIndex, endIndex);
  }, [data, filteredData, currentPage, rowsPerPage, paginationData]);

  const handleSort = useCallback(
    (key) => {
      setSortConfig((prev) => {
        const currentSort = prev?.[0];
        const newDirection =
          currentSort?.key === key && currentSort.direction === "asc"
            ? "desc"
            : "asc";
        const newSortConfig = [{ key, direction: newDirection }];

        updateURL({ sortConfig: newSortConfig, currentPage: 1 });
        if (onSortChange) {
          onSortChange(key, newDirection);
        }
        return newSortConfig;
      });
      setCurrentPage(1);
    },
    [updateURL, onSortChange]
  );

  const handlePendingFilterChange = useCallback((key, value) => {
    setPendingFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const togglePendingColumnVisibility = useCallback((key) => {
    setPendingColumns((prev) =>
      prev.map((col) => {
        if (col.key === key && !col.mandatory) {
          return { ...col, visible: !col.visible };
        }
        return col;
      })
    );
  }, []);

  const handleSelectAllPendingColumns = useCallback((event) => {
    const isChecked = event.target.checked;
    setPendingColumns((prev) =>
      prev.map((col) => ({
        ...col,
        visible: col.mandatory ? true : isChecked,
      }))
    );
  }, []);

  const togglePendingColumnPinned = useCallback((key) => {
    setPendingColumns((prev) =>
      prev.map((col) => {
        if (col.key === key) {
          return { ...col, pinned: col.pinned === "left" ? undefined : "left" };
        }
        return col;
      })
    );
  }, []);

  const allPendingColumnsVisible = useMemo(() => {
    const nonMandatoryColumns = pendingColumns.filter((col) => !col.mandatory);
    return (
      nonMandatoryColumns.length > 0 &&
      nonMandatoryColumns.every((col) => col.visible)
    );
  }, [pendingColumns]);

  const handleColumnReorder = useCallback(
    (dragIndex, hoverIndex, { ignorePinned = false } = {}) => {
      const visibleCols = columns.filter((col) => col.visible);

      const dragColumn = visibleCols[dragIndex];
      const hoverColumn = visibleCols[hoverIndex];

      if (!dragColumn || !hoverColumn) {
        return;
      }

      // On mobile there are no pinned columns, so every column can be moved.
      if (
        !ignorePinned &&
        (dragColumn?.pinned === "left" || hoverColumn?.pinned === "left")
      ) {
        return;
      }

      const dragColumnIndex = columns.findIndex(
        (col) => col.key === dragColumn.key
      );
      const hoverColumnIndex = columns.findIndex(
        (col) => col.key === hoverColumn.key
      );

      const newColumns = [...columns];
      newColumns.splice(dragColumnIndex, 1);
      newColumns.splice(hoverColumnIndex, 0, dragColumn);

      setColumns(newColumns);
    },
    [columns]
  );

  // --- Touch-based column drag & drop (mobile) ---
  // HTML5 drag events don't fire on touch screens, so header cells track
  // finger position manually and reorder columns on touch end.
  const touchDragIndexRef = useRef(null);
  const [touchDropIndex, setTouchDropIndex] = useState(null);

  const handleHeaderTouchStart = useCallback((index) => {
    touchDragIndexRef.current = index;
    setDraggedColumnIndex(index);
  }, []);

  const handleHeaderTouchMove = useCallback((e) => {
    if (touchDragIndexRef.current === null) return;
    const touch = e.touches[0];
    const el = document.elementFromPoint(touch.clientX, touch.clientY);
    const cell = el?.closest?.("[data-col-index]");
    setTouchDropIndex(
      cell ? Number(cell.getAttribute("data-col-index")) : null
    );
  }, []);

  const handleHeaderTouchEnd = useCallback(() => {
    const from = touchDragIndexRef.current;
    if (from !== null && touchDropIndex !== null && touchDropIndex !== from) {
      handleColumnReorder(from, touchDropIndex, { ignorePinned: true });
    }
    touchDragIndexRef.current = null;
    setTouchDropIndex(null);
    setDraggedColumnIndex(null);
  }, [touchDropIndex, handleColumnReorder]);

  const beginColumnResize = useCallback((key, clientX) => {
    setResizingColumnKey(key);
    initialMouseX.current = clientX;
    const column = columns.find((col) => col.key === key);
    initialColumnWidth.current = resolveColumnWidth(column?.width);
    const next = getNextVisibleColumn(columns, key);
    if (next) {
      initialNextColumnKey.current = next.key;
      initialNextColumnWidth.current = resolveColumnWidth(next.width);
    } else if (showActions) {
      initialNextColumnKey.current = ACTIONS_COL_KEY;
      initialNextColumnWidth.current = resolveColumnWidth(
        actionsColWidth,
        DEFAULT_ACTIONS_COL_WIDTH
      );
    } else {
      initialNextColumnKey.current = null;
      initialNextColumnWidth.current = 0;
    }
  }, [columns, showActions, actionsColWidth]);

  const handleMouseDown = useCallback(
    (e, key) => {
      e.preventDefault();
      e.stopPropagation();
      beginColumnResize(key, e.clientX);
    },
    [beginColumnResize]
  );

  useEffect(() => {
    if (!resizingColumnKey) return;
    const applyDelta = (deltaX) => {
      const nextKey = initialNextColumnKey.current;
      if (nextKey === ACTIONS_COL_KEY) {
        let newCurrent = Math.max(
          MIN_COL_WIDTH,
          initialColumnWidth.current + deltaX
        );
        let applied = newCurrent - initialColumnWidth.current;
        let newActions = initialNextColumnWidth.current - applied;
        if (newActions < MIN_COL_WIDTH) {
          newActions = MIN_COL_WIDTH;
          applied = initialNextColumnWidth.current - MIN_COL_WIDTH;
          newCurrent = initialColumnWidth.current + applied;
        }
        setActionsColWidth(newActions);
        setColumns((prevColumns) =>
          prevColumns.map((col) =>
            col.key === resizingColumnKey ? { ...col, width: newCurrent } : col
          )
        );
        return;
      }
      setColumns((prevColumns) =>
        resizeAdjacentColumns(
          prevColumns,
          resizingColumnKey,
          nextKey,
          initialColumnWidth.current,
          initialNextColumnWidth.current,
          deltaX
        )
      );
    };

    const handleMouseMove = (e) => {
      applyDelta(e.clientX - initialMouseX.current);
    };

    const handleTouchMove = (e) => {
      const touch = e.touches?.[0];
      if (!touch) return;
      e.preventDefault();
      applyDelta(touch.clientX - initialMouseX.current);
    };

    const handleMouseUp = () => {
      setResizingColumnKey(null);
      initialNextColumnKey.current = null;
      document.body.style.cursor = "default";
      document.body.style.userSelect = "";
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    document.addEventListener("touchmove", handleTouchMove, { passive: false });
    document.addEventListener("touchend", handleMouseUp);
    document.addEventListener("touchcancel", handleMouseUp);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleMouseUp);
      document.removeEventListener("touchcancel", handleMouseUp);
      document.body.style.cursor = "default";
      document.body.style.userSelect = "";
    };
  }, [resizingColumnKey]);

  const applyFilters = useCallback(() => {
    // This is now handled by handleApplyFilterBuilderFilters
    // Keeping for backward compatibility if needed
    setFilters(pendingFilters);
    setCurrentPage(1);
    setShowFilters(false);
    updateURL({ filters: pendingFilters, currentPage: 1 });
  }, [pendingFilters, updateURL]);

  const clearAllFilters = useCallback(() => {
    setFilters({});
    setPendingFilters({});
    setPendingFilterBuilderFilters([]);
    setCurrentPage(1);
    updateURL({ filters: {}, filterOperators: {}, currentPage: 1 });
    onFilterChange?.([]);
  }, [updateURL, onFilterChange]);

  const handleSaveView = useCallback(
    async (viewData) => {
      const org = userData?.organization;
      const applicationUserId = userData?.application_user_id;

      if (!org?.organization_id || !applicationUserId) {
        toast.error("User or organization information is missing");
        throw new Error("User or organization information is missing");
      }

      try {
        // Prepare view configuration
        const viewConfiguration = {
          filters: viewData.filters || [],
          sortConfig: viewData.sortConfig || sortConfig,
          columns:
            viewData.columns ||
            columns.map((col, index) => ({
              key: col.key || col.field,
              field: col.field || col.key,
              label: col.label,
              width: col.width,
              visible: col.visible,
              pinned: col.pinned,
              order: index,
            })),
          rowsPerPage: rowsPerPage,
        };

        // Prepare request payload
        const payload = {
          organization_id: org.organization_id,
          organization_entity_id: org.organization_entity_id || null,
          application_user_id: applicationUserId,
          datagrid_key: viewData.datagridKey || tableName || "default",
          view_name: viewData.viewName,
          view_configuration: viewConfiguration,
          is_default: false, // You can add a checkbox in the modal if needed
        };

        const response = await fetch(
          `${MAIN_URL}/api/organization-datagrid-user-views`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
            body: JSON.stringify(payload),
          }
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(
            errorData.error || `HTTP error! status: ${response.status}`
          );
        }

        const result = await response.json();
        toast.success("View saved successfully!");

        // Trigger refresh of saved views
        setViewSavedTrigger((prev) => prev + 1);

        return result;
      } catch (error) {
        console.error("Error saving view:", error);
        toast.error(error.message || "Failed to save view");
        throw error;
      }
    },
    [userData, sortConfig, columns, rowsPerPage, tableName]
  );

  // Helper to parse date string to Date object
  const parseDateValue = useCallback((dateString) => {
    if (!dateString || typeof dateString !== "string") return null;
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return null;
      }
      return date;
    } catch (e) {
      return null;
    }
  }, []);

  // Helper to check if a field is likely a date field based on field name
  const isDateField = useCallback((fieldName) => {
    const lowerFieldName = fieldName.toLowerCase();
    return (
      lowerFieldName.includes("date") ||
      lowerFieldName === "date_of_birth" ||
      lowerFieldName === "date_of_joining" ||
      lowerFieldName === "date_of_birth" ||
      lowerFieldName === "joining_date" ||
      lowerFieldName === "dob"
    );
  }, []);

  const buildFilterBuilderFiltersFromSearchParams = useCallback(
    (paramsToUse) => {
      const source = paramsToUse || searchParams;
      const fields = new Set();

      source.forEach((_, key) => {
        if (!key.startsWith("filter_")) return;
        if (key.endsWith("_logic")) return;
        if (key.endsWith("_operator")) {
          fields.add(key.slice("filter_".length, -"_operator".length));
          return;
        }
        fields.add(key.slice("filter_".length));
      });

      const result = [];
      fields.forEach((field) => {
        if (!field) return;
        const operator =
          source.get(`filter_${field}_operator`) || OPERATORS.CONTAINS;
        const rawValue = source.get(`filter_${field}`);
        const requiresValue = operatorRequiresValue(operator);

        if (!requiresValue) {
          result.push({ field, operator, value: null });
          return;
        }

        if (rawValue === null || rawValue === undefined || rawValue === "") {
          return;
        }

        result.push({ field, operator, value: rawValue });
      });

      return result;
    },
    [searchParams]
  );

  // Convert simple filters {key: value} to FilterBuilder format [{field, operator, value}]
  const convertToFilterBuilderFormat = useCallback(
    (simpleFilters, searchParamsForOperators = null) => {
      const paramsToUse = searchParamsForOperators || searchParams;

      return Object.entries(simpleFilters)
        .filter(([key, value]) => {
          // Read operator from URL params first to check if it requires a value
          const operatorKey = `filter_${key}_operator`;
          const operator = paramsToUse.get(operatorKey) || OPERATORS.CONTAINS;
          const requiresValue = operatorRequiresValue(operator);

          // Include filter if:
          // 1. Operator doesn't require value (today, this_week, this_month, is_null, is_not_null)
          // 2. Or value is valid (not null, undefined, or empty string)
          return (
            !requiresValue ||
            (value !== null && value !== undefined && value !== "")
          );
        })
        .map(([key, value], index) => {
          // Read operator from URL params
          const operatorKey = `filter_${key}_operator`;
          let operator = paramsToUse.get(operatorKey) || OPERATORS.CONTAINS; // Default to contains
          const requiresValue = operatorRequiresValue(operator);

          // For operators that don't require values, set value to null
          if (!requiresValue) {
            return {
              field: key,
              operator: operator,
              value: null,
            };
          }

          // Check if operator requires multiple values (array)
          const requiresMultipleValues =
            operatorRequiresMultipleValues(operator);

          // Parse date values to Date objects for DatePicker
          let parsedValue = value;

          // Handle array values (for BETWEEN, IN, NOT_IN operators)
          if (requiresMultipleValues && typeof value === "string") {
            // Split comma-separated string back to array
            const arrayValues = value
              .split(",")
              .map((v) => v.trim())
              .filter((v) => v !== "");

            if (isDateField(key) && operator === OPERATORS.BETWEEN) {
              // Parse date array for BETWEEN operator
              parsedValue = arrayValues.map((dateStr) => {
                const dateValue = parseDateValue(dateStr);
                return dateValue || dateStr; // Return Date object or original string if parsing fails
              });
            } else {
              // For IN, NOT_IN operators, keep as string array
              parsedValue = arrayValues;
            }
          } else if (isDateField(key) && !requiresMultipleValues) {
            // Single date value
            const dateValue = parseDateValue(String(value));
            if (dateValue) {
              parsedValue = dateValue;
              // If no operator specified for date, default to 'on'
              if (!paramsToUse.get(operatorKey)) {
                operator = OPERATORS.ON;
              }
            }
          } else if (typeof value === "string" && value.trim() !== "") {
            parsedValue = value.trim();
          }

          return {
            field: key,
            operator: operator,
            value: parsedValue,
          };
        });
    },
    [searchParams, isDateField, parseDateValue]
  );

  // Helper function to format date to YYYY-MM-DD without timezone issues
  const formatDateForFilter = (date) => {
    if (!(date instanceof Date)) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Convert FilterBuilder format [{field, operator, value}] to simple filters {key: value}
  // Returns object with filters and operators
  const convertFromFilterBuilderFormat = useCallback((filterBuilderFilters) => {
    const simpleFilters = {};
    const filterOperators = {};

    filterBuilderFilters.forEach((filter, index) => {
      if (!filter.field || !filter.operator) return;

      // Check if operator requires a value
      const requiresValue = operatorRequiresValue(filter.operator);

      // For operators that don't require values (today, this_week, this_month, is_null, is_not_null)
      // Allow null values and still include the filter
      if (!requiresValue) {
        // Store operator even if value is null
        filterOperators[filter.field] = filter.operator;
        // Store a special marker or empty string for URL compatibility
        simpleFilters[filter.field] = "";
        return;
      }

      // For operators that require values, check if value is valid
      const isValidValue =
        filter.value !== null &&
        filter.value !== undefined &&
        filter.value !== "" &&
        !(Array.isArray(filter.value) && filter.value.length === 0);

      if (isValidValue) {
        // Convert date objects to strings for URL storage
        let valueToStore = filter.value;
        if (filter.value instanceof Date) {
          // Use YYYY-MM-DD format to avoid timezone issues
          valueToStore = formatDateForFilter(filter.value);
        } else if (
          Array.isArray(filter.value) &&
          filter.value.length > 0 &&
          filter.value[0] instanceof Date
        ) {
          // Handle date arrays (for BETWEEN operator)
          valueToStore = filter.value
            .map((d) => (d instanceof Date ? formatDateForFilter(d) : d))
            .join(",");
        } else if (Array.isArray(filter.value)) {
          // Handle other array values (for IN, NOT_IN operators)
          valueToStore = filter.value.map((v) => String(v)).join(",");
        } else {
          valueToStore = String(filter.value);
        }

        simpleFilters[filter.field] = valueToStore;

        // Store operator if it's not the default (contains)
        // Also store operators for date fields and other special cases
        if (filter.operator && filter.operator !== OPERATORS.CONTAINS) {
          filterOperators[filter.field] = filter.operator;
        } else if (filter.operator) {
          // Store all operators, not just non-default ones
          filterOperators[filter.field] = filter.operator;
        }
      }
    });

    return {
      filters: simpleFilters,
      operators: filterOperators,
    };
  }, []);

  // Add function to load a saved view
  const handleLoadSavedView = useCallback(
    (view) => {
      if (!view?.view_configuration) {
        toast.error("Invalid view configuration");
        return;
      }

      const config = view.view_configuration;

      // Prepare URL updates object to combine all updates
      const urlUpdates = {
        currentPage: 1,
      };

      // Load filters
      if (
        config.filters &&
        Array.isArray(config.filters) &&
        config.filters.length > 0
      ) {
        setPendingFilterBuilderFilters(config.filters);
        // Convert to simple format and apply
        const { filters: simpleFilters, operators: filterOperators } =
          convertFromFilterBuilderFormat(config.filters);
        setFilters(simpleFilters);
        setPendingFilters(simpleFilters);

        urlUpdates.filters = simpleFilters;
        urlUpdates.filterOperators = filterOperators;

        // Notify parent with FilterBuilder format (for API calls if needed)
        // Ensure filters have value: null for operators that don't require values
        const filtersForAPI = config.filters.map((filter) => {
          const requiresValue = operatorRequiresValue(filter.operator);
          return {
            field: filter.field,
            operator: filter.operator,
            value: requiresValue
              ? filter.value !== undefined
                ? filter.value
                : null
              : null,
          };
        });
        onFilterChange?.(filtersForAPI);
      } else {
        // If no filters, clear them
        urlUpdates.filters = {};
        urlUpdates.filterOperators = {};
        onFilterChange?.([]);
      }

      // Load sort configuration
      if (
        config.sortConfig &&
        Array.isArray(config.sortConfig) &&
        config.sortConfig.length > 0
      ) {
        setSortConfig(config.sortConfig);
        urlUpdates.sortConfig = config.sortConfig;
      }

      // Load rows per page
      if (config.rowsPerPage) {
        setRowsPerPage(config.rowsPerPage);
        urlUpdates.rowsPerPage = config.rowsPerPage;
      }

      // Update URL with all changes at once
      updateURL(urlUpdates);

      // Load columns (this doesn't affect URL, so it's separate)
      if (
        config.columns &&
        Array.isArray(config.columns) &&
        config.columns.length > 0
      ) {
        // Map saved columns back to column format
        const loadedColumns = config.columns
          .map((savedCol) => ({
            key: savedCol.key || savedCol.field,
            field: savedCol.field || savedCol.key,
            label: savedCol.label,
            width: savedCol.width || 150,
            visible: savedCol.visible !== undefined ? savedCol.visible : true,
            pinned: savedCol.pinned || false,
            order: savedCol.order || 0,
          }))
          .sort((a, b) => (a.order || 0) - (b.order || 0));

        setColumns(loadedColumns);
      }

      setCurrentPage(1);
      toast.success(`View "${view.view_name}" loaded successfully!`);
    },
    [convertFromFilterBuilderFormat, updateURL, onFilterChange]
  );

  // Helper function to load filters from loadedBackendConfig
  // Handles both FilterBuilder format (array) and simple format (object)
  const loadFiltersFromConfig = useCallback(
    (configFilters) => {
      if (!configFilters) return;

      // Check if filters are in FilterBuilder format (array)
      if (Array.isArray(configFilters) && configFilters.length > 0) {
        // Convert FilterBuilder format to simple format
        const { filters: simpleFilters, operators: filterOperators } =
          convertFromFilterBuilderFormat(configFilters);

        setFilters(simpleFilters);
        setPendingFilters(simpleFilters);

        // Update URL params with the converted filters
        updateURL({
          filters: simpleFilters,
          filterOperators: filterOperators,
        });

        console.log("Loaded filters from FilterBuilder format:", {
          filterBuilder: configFilters,
          simple: simpleFilters,
          operators: filterOperators,
        });
      } else if (
        typeof configFilters === "object" &&
        !Array.isArray(configFilters)
      ) {
        // Already in simple format (object)
        setFilters(configFilters);
        setPendingFilters(configFilters);
        console.log("Loaded filters from simple format:", configFilters);
      }
    },
    [convertFromFilterBuilderFormat, updateURL]
  );

  // Load filters from loadedBackendConfig
  // This useEffect is placed after loadFiltersFromConfig is defined to avoid initialization order issues
  useEffect(() => {
    if (!loadedBackendConfig?.filters) return;
    const urlAlreadyHasFilters = Array.from(searchParams.keys()).some((key) =>
      key.startsWith("filter_")
    );
    if (urlAlreadyHasFilters) return;
    loadFiltersFromConfig(loadedBackendConfig.filters);
  }, [loadedBackendConfig?.filters, loadFiltersFromConfig, searchParams]);

  const handleFilterDialogOpen = useCallback(() => {
    const fromUrl = buildFilterBuilderFiltersFromSearchParams(searchParams);
    const filterBuilderFilters =
      fromUrl.length > 0
        ? fromUrl
        : convertToFilterBuilderFormat(filters, searchParams);

    const { filters: simpleFilters } = convertFromFilterBuilderFormat(
      filterBuilderFilters
    );
    setPendingFilters(simpleFilters);
    setFilters((prev) =>
      JSON.stringify(prev) === JSON.stringify(simpleFilters) ? prev : simpleFilters
    );
    setPendingFilterBuilderFilters(filterBuilderFilters);
    setShowFilters(true);
  }, [
    filters,
    searchParams,
    buildFilterBuilderFiltersFromSearchParams,
    convertToFilterBuilderFormat,
    convertFromFilterBuilderFormat,
  ]);

  const handleFilterDialogClose = useCallback(() => {
    setPendingFilters(filters);
    const filterBuilderFilters = convertToFilterBuilderFormat(filters);
    setPendingFilterBuilderFilters(filterBuilderFilters);
    setShowFilters(false);
  }, [filters, convertToFilterBuilderFormat]);

  const handleFilterBuilderChange = useCallback((newFilters) => {
    setPendingFilterBuilderFilters(newFilters);
  }, []);

  // Convert filters from URL to FilterBuilder format and notify parent when filters or searchParams change
  // This must be after convertToFilterBuilderFormat is defined
  useEffect(() => {
    // Wait for initialization to complete
    if (!hasInitializedFromURL.current) return;

    // Read current filters and operators from URL params (in case they changed externally)
    const currentFilters = {};
    const currentOperators = {};
    searchParams.forEach((value, key) => {
      if (key.startsWith("filter_") && !key.endsWith("_operator")) {
        const filterKey = key.replace("filter_", "");
        const operatorKey = `filter_${filterKey}_operator`;
        const operator = searchParams.get(operatorKey);
        const requiresValue = operator ? operatorRequiresValue(operator) : true;

        if (value || !requiresValue) {
          currentFilters[filterKey] = value || "";
          if (operator) {
            currentOperators[filterKey] = operator;
          }
        }
      }
    });

    // Update local filters state if URL changed
    if (JSON.stringify(currentFilters) !== JSON.stringify(filters)) {
      setFilters(currentFilters);
    }

    // Only proceed if we have filters to convert
    if (
      Object.keys(currentFilters).length === 0 &&
      Object.keys(currentOperators).length === 0
    ) {
      // No filters, notify parent with empty array
      onFilterChange?.([]);
      return;
    }

    // Convert simple filters from URL to FilterBuilder format
    const filterBuilderFilters = convertToFilterBuilderFormat(currentFilters);

    // Normalize filters for API - ensure value: null for operators that don't require values
    const filtersForAPI = filterBuilderFilters.map((filter) => {
      const requiresValue = operatorRequiresValue(filter.operator);
      return {
        field: filter.field,
        operator: filter.operator,
        value: requiresValue
          ? filter.value !== undefined
            ? filter.value
            : null
          : null,
      };
    });

    // Notify parent component with FilterBuilder format filters
    onFilterChange?.(filtersForAPI);
    // Intentionally omit `filters` from deps — including it re-ran this effect after
    // setFilters() on Apply before the URL searchParams finished updating, which
    // called onFilterChange([]) and wiped the just-applied filters.
  }, [searchParams, convertToFilterBuilderFormat, onFilterChange]);

  const handleApplyFilterBuilderFilters = useCallback((filtersFromBuilder) => {
    // Prefer the filters passed by FilterBuilder (current UI state). Relying only on
    // pendingFilterBuilderFilters can apply stale/empty filters if the parent sync lags.
    const sourceFilters = Array.isArray(filtersFromBuilder)
      ? filtersFromBuilder
      : pendingFilterBuilderFilters;

    const { filters: simpleFilters, operators: filterOperators } =
      convertFromFilterBuilderFormat(sourceFilters);

    setPendingFilterBuilderFilters(sourceFilters);
    setFilters(simpleFilters);
    setPendingFilters(simpleFilters);
    setCurrentPage(1);
    setShowFilters(false);
    updateURL({
      filters: simpleFilters,
      filterOperators: filterOperators,
      currentPage: 1,
    });

    // Notify parent with FilterBuilder format (for API calls if needed)
    const filtersForAPI = sourceFilters.map((filter) => {
      const requiresValue = operatorRequiresValue(filter.operator);
      return {
        field: filter.field,
        operator: filter.operator,
        value: requiresValue
          ? filter.value !== undefined
            ? filter.value
            : null
          : null,
      };
    });
    onFilterChange?.(filtersForAPI);
  }, [
    pendingFilterBuilderFilters,
    convertFromFilterBuilderFormat,
    updateURL,
    onFilterChange,
  ]);

  const handleColumnDialogOpen = useCallback(() => {
    const columnsWithCurrentVisibility = originalColumnOrder.map((origCol) => {
      const currentCol = columns.find((col) => col.key === origCol.key);
      return {
        ...origCol,
        visible: currentCol?.visible ?? origCol.visible,
        mandatory: origCol.mandatory ?? false,
      };
    });
    setPendingColumns(columnsWithCurrentVisibility);
    setColumnManagerOpen(true);
  }, [originalColumnOrder, columns]);

  const handleColumnDialogClose = useCallback(() => {
    setPendingColumns([...columns]);
    setColumnManagerOpen(false);
  }, [columns]);

  const applyColumnChanges = useCallback(() => {
    setColumns([...pendingColumns]);
    setColumnManagerOpen(false);
  }, [pendingColumns]);

  const cancelColumnChanges = useCallback(() => {
    setPendingColumns([...columns]);
    setColumnManagerOpen(false);
  }, [columns]);

  const visibleColumns = useMemo(() => {
    const visible = columns.filter((col) => col.visible);
    return visible.sort((a, b) => {
      if (a.pinned === "left" && b.pinned !== "left") return -1;
      if (a.pinned !== "left" && b.pinned === "left") return 1;
      return (a.order ?? 0) - (b.order ?? 0);
    });
  }, [columns]);

  const tableContentWidth = useMemo(() => {
    const columnsWidth = visibleColumns.reduce(
      (total, col) => total + resolveColumnWidth(col.width),
      0
    );
    const actionsWidth = showActions
      ? resolveColumnWidth(actionsColWidth, DEFAULT_ACTIONS_COL_WIDTH)
      : 0;
    return columnsWidth + actionsWidth;
  }, [visibleColumns, showActions, actionsColWidth]);

  const desktopTableWidth = tableContentWidth + CHECKBOX_COL_WIDTH;
  const resolvedActionsWidth = showActions
    ? resolveColumnWidth(actionsColWidth, DEFAULT_ACTIONS_COL_WIDTH)
    : 0;

  const tableSizeStyle = useMemo(
    () => ({
      tableLayout: "fixed",
      width: "100%",
      minWidth: `${desktopTableWidth}px`,
    }),
    [desktopTableWidth]
  );

  const activeFilters = useMemo(
    () => Object.keys(filters).filter((key) => filters[key]?.length),
    [filters]
  );
  const canEditAction = EditFunc !== undefined && EditFunc !== null;
  const canDeleteAction = DeleteFunc !== undefined && DeleteFunc !== null;

  const handleEdit = useCallback(
    (item) => {
      if (!canEditAction) {
        return;
      }
      if (EditFunc) {
        EditFunc(item);
      } else {
        navigate(`${Route}/edit/${item?.[mainKey]}`);
      }
    },
    [EditFunc, canEditAction, navigate, Route, mainKey]
  );

  const handleShowData = useCallback(
    (item) => {
      if (handleShow) {
        handleShow(item);
      } else {
        navigate(`${Route}/view/${item?.[mainKey]}`);
      }
    },
    [handleShow, navigate, Route, mainKey]
  );

  const handleDelete = useCallback((item) => {
    if (!canDeleteAction) {
      return;
    }
    if (isUsersListTable(tableName) && isLoggedInUserRow(item, userData)) {
      toast.error("You cannot delete your own account.");
      return;
    }
    setSelectedItem(item);
    setDeleteDialogOpen(true);
  }, [canDeleteAction, tableName, userData]);

  const handleDeleteConfirm = useCallback(async (restoreEmployee = false) => {
    if (!selectedItem) {
      setDeleteDialogOpen(false);
      return;
    }

    const itemKey = selectedItem?.[mainKey];
    setDeleteDialogOpen(false);

    try {
      if (DeleteFunc) {
        await DeleteFunc(itemKey, {
          restore_employee: !!restoreEmployee,
        });
      }

      deletedIdsRef.current.add(itemKey);
      const remainingRows = data.filter(
        (item) => item?.[mainKey] !== itemKey
      ).length;
      setData((prevData) =>
        prevData.filter((item) => item?.[mainKey] !== itemKey)
      );

      if (typeof setPaginationData === "function") {
        // Parent owns the pagination state: recompute all counters.
        setPaginationData((prev) => {
          if (!prev) return prev;
          const pageSize = prev.pageSize || prev.limit || rowsPerPage || 10;
          const total = Math.max(0, (prev.total ?? 1) - 1);
          const newTotalPages = Math.max(1, Math.ceil(total / pageSize));
          return {
            ...prev,
            total,
            totalPages: newTotalPages,
            hasNext: currentPage < newTotalPages,
            hasPrev: currentPage > 1,
          };
        });
      } else if (paginationData) {
        // Server-side pagination without parent setter: fix counters locally.
        setDeleteAdjustment((prev) => prev + 1);
      }

      // If we emptied the current page, jump back one page (triggers refetch via URL).
      if (paginationData && remainingRows === 0 && currentPage > 1) {
        updateURL({ currentPage: currentPage - 1 });
        setCurrentPage(currentPage - 1);
      }

      onAfterDelete?.(itemKey);
    } catch (error) {
      console.error("Delete Error:", error);
      // DeleteFunc usually shows its own toast; avoid a second generic message.
      if (!error?.response && error?.message !== "Not allowed") {
        toast.error("Failed to delete. Please try again.");
      }
    } finally {
      setSelectedItem(null);
    }
  }, [selectedItem, DeleteFunc, mainKey, data, onAfterDelete, setPaginationData, paginationData, currentPage, rowsPerPage, updateURL]);

  /** Toggle in front of (left of) view/edit/delete icons, same row. */
  const wrapActionCellWithActiveToggle = useCallback(
    (row, iconRowJsx) => {
      const icons = (
        <div className="ems-row-actions">{iconRowJsx}</div>
      );
      if (!toggleActiveFn) {
        return icons;
      }
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 0.25,
            flexWrap: "nowrap",
          }}
        >
          <Tooltip title={
            isUsersListTable(tableName) && isLoggedInUserRow(row, userData)
              ? "You cannot deactivate your own account"
              : getToggleActiveTooltip(row)
          }>
            <Box
              component="span"
              onClick={(e) => e.stopPropagation()}
              sx={{ display: "flex", alignItems: "center" }}
            >
              <Switch
                size="small"
                checked={getRowActiveForToggle(row)}
                disabled={
                  isToggleActiveDisabled(row) ||
                  (isUsersListTable(tableName) && isLoggedInUserRow(row, userData))
                }
                onChange={(e) => {
                  e.stopPropagation();
                  if (
                    isToggleActiveDisabled(row) ||
                    (isUsersListTable(tableName) && isLoggedInUserRow(row, userData))
                  ) {
                    return;
                  }
                  toggleActiveFn(row, e.target.checked);
                }}
                inputProps={{ "aria-label": "Active" }}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: "var(--ems-green)",
                    "&:hover": {
                      backgroundColor: "var(--ems-green-tint)",
                    },
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: "var(--ems-green)",
                    opacity: 0.45,
                  },
                }}
              />
            </Box>
          </Tooltip>
          {icons}
        </Box>
      );
    },
    [toggleActiveFn, tableName, userData]
  );

  const renderCardActions = (row) => {
    if (!showActions) return null;
    return wrapActionCellWithActiveToggle(
      row,
      <>
        <Tooltip title="Show">
          <IconButton
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              handleShowData(row);
            }}
            sx={actionBtnSx}
          >
            <Eye size={18} />
          </IconButton>
        </Tooltip>
        {canEditAction && (
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                handleEdit(row);
              }}
              sx={actionBtnSx}
            >
              <Edit size={18} />
            </IconButton>
          </Tooltip>
        )}
        {canDeleteAction && (
          <UsersDeleteButton
            row={row}
            userData={userData}
            tableName={tableName}
            onDelete={handleDelete}
            sx={actionBtnSx}
            stopPropagation
          />
        )}
        {typeof additionalActions === "function" && additionalActions(row)}
      </>
    );
  };

  const DynamicUserCard = ({ item, index }) => {
    const visibleCols = visibleColumns;
    return (
      <Card
        sx={{
          mb: 2,
          borderRadius: 2,
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          "&:hover": {
            boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
          },
          transition: "box-shadow 0.2s ease-in-out",
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {visibleCols
                .filter((col) => col.key)
                .map((col) => {
                  const cellContent = renderDataCellContent(item, col);

                  return (
                    <Box
                      key={col.key}
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1.5,
                        py: 0.5,
                      }}
                    >
                      <Box
                        sx={{
                          color: colors.text.secondary,
                          mt: 0.25,
                          minWidth: 20,
                          display: "flex",
                          justifyContent: "center",
                        }}
                      ></Box>

                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            color: colors.text.secondary,
                            fontSize: "0.75rem",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                            display: "block",
                            mb: 0.25,
                          }}
                        >
                          {col.label}
                        </Typography>
                        <Typography
                          variant="body2"
                          component="div"
                          sx={{
                            color: colors.text.primary,
                            fontSize: "0.875rem",
                            wordBreak: "break-word",
                            lineHeight: 1.4,
                          }}
                        >
                          {cellContent}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
            </Box>

            {showActions && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  mt: 1,
                  gap: 1,
                }}
              >
                {wrapActionCellWithActiveToggle(
                  item,
                  <>
                    <Tooltip title="Show">
                      <IconButton
                        size="small"
                        onClick={() => handleShowData(item)}
                        sx={actionBtnSx}
                      >
                        <Eye size={16} />
                      </IconButton>
                    </Tooltip>

                    {canEditAction && (
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(item)}
                          sx={actionBtnSx}
                        >
                          <Edit size={16} />
                        </IconButton>
                      </Tooltip>
                    )}
                    {canDeleteAction && (
                      <UsersDeleteButton
                        row={item}
                        userData={userData}
                        tableName={tableName}
                        onDelete={handleDelete}
                        sx={actionBtnSx}
                      />
                    )}
                    {typeof additionalActions === "function" &&
                      additionalActions(item)}
                  </>
                )}
                <Chip
                  label={`#${(currentPage - 1) * rowsPerPage + index + 1}`}
                  size="small"
                  sx={{
                    backgroundColor: colors.grey[200],
                    color: colors.text.secondary,
                    fontSize: "0.7rem",
                    height: 20,
                  }}
                />
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>
    );
  };

  useEffect(() => {
    if (snackbar.open) {
      const timer = setTimeout(() => {
        setSnackbar((prev) => ({ ...prev, open: false }));
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [snackbar.open]);

  return (
    <Box
      sx={{
        p: 0,
        maxWidth: "100%",
        margin: "0 auto",
        fontFamily: '"Manrope", sans-serif',
        minHeight: 0,
      }}
    >
      {!hideToolbar && (
        <Toolbar
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          view={view}
          setView={setView}
          colors={colors}
          isSmallScreen={isSmallScreen}
          // Export dependencies
          columns={columns}
          filteredData={filteredData}
          getNestedValue={getNestedValue}
          formatLabel={formatLabel}
          userData={userData}
          tableName={tableName}
          heading={listHeading}
          recordKey={recordKey}
          recordLabel={recordLabel}
          // Search dependencies
          updateURL={updateURL}
          setCurrentPage={setCurrentPage}
          // Filter dependencies
          loadedBackendConfig={loadedBackendConfig}
          convertToFilterBuilderFormat={convertToFilterBuilderFormat}
          setPendingFilters={setPendingFilters}
          setPendingFilterBuilderFilters={setPendingFilterBuilderFilters}
          setLockedFilters={setLockedFilters}
          setShowFilters={setShowFilters}
          handleFilterDialogOpen={handleFilterDialogOpen}
          // Print dependencies
          data={data}
          toolbarExtras={toolbarExtras}
        />
      )}

      {/* Active Filters Bar */}
      <ActiveFiltersBar configss={configss} />

      {/* Saved Views Bar */}
      <SavedViewsBar
        datagridKey={tableName}
        onLoadView={handleLoadSavedView}
        onViewSaved={viewSavedTrigger}
      />

      {showFilters && (
        <div className="ems-flt-backdrop" onClick={handleFilterDialogClose}>
          <div
            className="ems-flt-modal"
            role="dialog"
            aria-label="Filters"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ems-flt-head">
              <div className="ems-flt-head-title">
                <Filter size={18} />
                Filters
                {pendingFilterBuilderFilters.length > 0 && (
                  <span className="ems-flt-head-count">
                    {pendingFilterBuilderFilters.length}
                  </span>
                )}
              </div>
              <button
                className="ems-icon-btn"
                title="Close"
                aria-label="Close"
                onClick={handleFilterDialogClose}
              >
                <X size={20} />
              </button>
            </div>

            {filterModule || tableName ? (
              <FilterBuilder
                key={`filter-builder-${tableName || filterModule || "default"}-${pendingFilterBuilderFilters.map((f) => `${f.field}:${f.operator}`).join("|")}`}
                module={filterModule || tableName}
                onFiltersChange={handleFilterBuilderChange}
                onApplyFilters={handleApplyFilterBuilderFilters}
                onClearFilters={clearAllFilters}
                showApplyButton={true}
                showClearButton={true}
                initialFilters={pendingFilterBuilderFilters}
                onSaveView={handleSaveView}
                columns={columns}
                sortConfig={sortConfig}
                datagridKey={tableName}
              />
            ) : (
              <>
                <div className="ems-flt-body">
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
                      gap: 2,
                    }}
                  >
                    {visibleColumns
                      .filter((col) => col.filterable !== false)
                      .map((col) => {
                        const isLocked = lockedFilters[col.key];

                        return (
                          <TextField
                            key={col.key}
                            label={formatLabel(col.label)}
                            variant="outlined"
                            size="small"
                            value={pendingFilters[col.key] || ""}
                            onChange={(e) => {
                              if (!isLocked) {
                                handlePendingFilterChange(col.key, e.target.value);
                              }
                            }}
                            InputProps={{
                              startAdornment: (
                                <Search
                                  size={16}
                                  style={{
                                    marginRight: "8px",
                                    color: colors.grey[800],
                                  }}
                                />
                              ),
                              readOnly: isLocked, // prevents typing
                            }}
                            disabled={isLocked} // visually disables input
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                backgroundColor: isLocked
                                  ? theme.palette.mode === "dark"
                                    ? colors.grey[800]
                                    : colors.grey[200]
                                  : colors.surface,
                              },
                              "& .MuiInputLabel-root.Mui-disabled": {
                                color: colors.text.secondary,
                              },
                            }}
                          />
                        );
                      })}
                  </Box>
                </div>
                <div className="ems-flt-foot">
                  <button className="ems-btn ems-ghost" onClick={clearAllFilters}>
                    <X size={16} />
                    Clear All Filters
                  </button>
                  <div className="ems-flt-spacer" />
                  <button className="ems-btn ems-primary" onClick={applyFilters}>
                    <Filter size={16} />
                    Apply Filters
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {columnManagerOpen && (
        <Dialog
          open={columnManagerOpen}
          onClose={handleColumnDialogClose}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle
            sx={{
              backgroundColor: colors.grey[300],
              color: colors.text.primary,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",

                alignItems: "center",
              }}
            >
              <Typography
                variant="h6"
                component="span"
                sx={{ fontWeight: 500 }}
              >
                Manage Columns
              </Typography>
              <IconButton
                title="Close"
                onClick={handleColumnDialogClose}
                sx={{ color: colors.text.primary }}
              >
                <X size={20} />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent
            sx={{
              p: 3,
              pb: 0,
              backgroundColor: colors.surface,
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={allPendingColumnsVisible}
                  onChange={handleSelectAllPendingColumns}
                  sx={{ color: colors.success }}
                />
              }
              label={
                <Typography
                  variant="body1"
                  sx={{ fontWeight: 500, color: colors.text.primary }}
                >
                  Select All
                </Typography>
              }
              sx={{ mb: 2 }}
            />
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                gap: 2,
                mb: 3,
              }}
            >
              {pendingColumns.map((col) => (
                <Box
                  key={col.key}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    p: 1.5,
                    borderRadius: 1,
                    backgroundColor: colors.grey[50],
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={col.visible}
                        onChange={() => togglePendingColumnVisibility(col.key)}
                        disabled={col.mandatory}
                        sx={{ color: colors.success }}
                      />
                    }
                    label={
                      <Typography
                        variant="body2"
                        sx={{ color: colors.text.primary }}
                      >
                        {formatLabel(col.label)}
                      </Typography>
                    }
                    sx={{ flex: 1, m: 0 }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => togglePendingColumnPinned(col.key)}
                    title={
                      col.pinned === "left" ? "Unpin column" : "Pin column"
                    }
                    sx={{
                      color:
                        col.pinned === "left"
                          ? colors.primary
                          : colors.text.secondary,
                      "&:hover": {
                        backgroundColor: colors.grey[200],
                      },
                    }}
                  >
                    <PushPinIcon
                      fontSize="small"
                      sx={{
                        transform:
                          col.pinned === "left"
                            ? "rotate(-20deg)"
                            : "rotate(45deg)",
                        transition: "transform 0.2s ease-in-out",
                      }}
                    />
                  </IconButton>
                </Box>
              ))}
            </Box>
            <Box
              sx={{
                display: "flex",
                gap: 1,
                justifyContent: "flex-start",
                flexWrap: "wrap",
                position: "sticky",
                background: "white",
                bottom: 0,
                p: 2,
              }}
            >
              <Button
                variant="contained"
                title="Apply changes"
                sx={{
                  backgroundColor: colors.primary,
                  color: "white",
                  "&:hover": {
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? theme.palette.primary.light
                        : theme.palette.primary.dark,
                  },
                }}
                startIcon={<Columns3 size={16} />}
                onClick={applyColumnChanges}
              >
                Apply
              </Button>
              <Button
                variant="outlined"
                title="Cancel changes"
                sx={{
                  color: colors.text.secondary,
                  borderColor: colors.text.secondary,
                  "&:hover": {
                    borderColor: colors.text.primary,
                    color: colors.text.primary,
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
                startIcon={<X size={16} />}
                onClick={cancelColumnChanges}
              >
                Cancel
              </Button>
            </Box>
          </DialogContent>
        </Dialog>
      )}

      {isSmallScreen ? (
        <Box>
          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: 4,
              }}
            >
              <CircularProgress size={24} sx={{ color: colors.primary }} />
              <Typography
                variant="body2"
                sx={{ mt: 1, ml: 2, color: colors.text.secondary }}
              >
                Loading data...
              </Typography>
            </Box>
          ) : paginatedData?.length === 0 ? (
            <EmptyTableState />
          ) : (
            <div className="ems-mcard-list">
              {paginatedData?.map((row, index) => (
                <ListRecordCard
                  key={row?.[mainKey] ?? index}
                  row={row}
                  columns={visibleColumns}
                  getNestedValue={getNestedValue}
                  renderCell={renderDataCellContent}
                  tableName={tableName}
                  showNameAvatar={showNameAvatar}
                  onOpen={() => handleShowData(row)}
                  actions={renderCardActions(row)}
                />
              ))}
            </div>
          )}
        </Box>
      ) : (
        <div
          className={
            view === "table"
              ? "ems-card ems-table-card"
              : "ems-table-card ems-grid-bg"
          }
        >
          {view === "table" ? (
            !loading && paginatedData?.length === 0 ? (
              <EmptyTableState asCard={false} />
            ) : (
            <TableContainer sx={{ width: "100%" }}>
              <Table
                className="ems-table-fixed-cols"
                style={tableSizeStyle}
                sx={{
                  tableLayout: "fixed",
                  width: "100%",
                  minWidth: `${desktopTableWidth}px`,
                }}
                aria-label="dynamic data grid"
              >
                <colgroup>
                  <col
                    style={{
                      width: toColPercent(
                        CHECKBOX_COL_WIDTH,
                        desktopTableWidth
                      ),
                    }}
                  />
                  {visibleColumns.map((col) => (
                    <col
                      key={`col-width-${col.key}`}
                      style={{
                        width: toColPercent(
                          resolveColumnWidth(col.width),
                          desktopTableWidth
                        ),
                      }}
                    />
                  ))}
                  {showActions ? (
                    <col
                      style={{
                        width: toColPercent(
                          resolvedActionsWidth,
                          desktopTableWidth
                        ),
                      }}
                    />
                  ) : null}
                </colgroup>
                {/* ---------- TABLE HEADER ---------- */}
                <TableHead>
                  <TableRow sx={{ backgroundColor: colors.grey[100] }}>
                    {/* ✅ Header Checkbox (Select All) */}

                    <TableCell
                      sx={{
                        p: "8px 8px",
                        width: 50,
                        textAlign: "center",
                        backgroundColor: colors.grey[100],
                      }}
                      onMouseEnter={() => setShowRowCheckboxes(true)}
                      onMouseLeave={() => setShowRowCheckboxes(false)}
                    >
                      <Checkbox
                        checked={
                          paginatedData.length > 0 &&
                          selectedRows.length === paginatedData.length
                        }
                        indeterminate={
                          selectedRows.length > 0 &&
                          selectedRows.length < paginatedData.length
                        }
                        onChange={(e) => {
                          e.stopPropagation();
                          handleSelectAll(e);
                        }}
                        sx={{
                          opacity:
                            showRowCheckboxes || selectedRows.length > 0
                              ? 1
                              : 0,
                          transition: "opacity 0.25s ease",
                        }}
                      />
                    </TableCell>

                    {/* ---------- HEADER COLUMNS ---------- */}
                    {visibleColumns.map((col, index) => (
                      <TableCell
                        key={col.key}
                        sx={{
                          p: "8px 16px",
                          textAlign: "left",
                          fontWeight: 650,
                          fontSize: "12px",
                          color: "var(--ems-faint)",
                          borderRight:
                            showActions && index === visibleColumns.length - 1
                              ? "none"
                              : `1px solid ${colors.grey[300]}`,
                          ...getColumnWidthSx(col.width),
                          position:
                            col.pinned === "left" ? "sticky" : "relative",
                          left: col.pinned === "left" ? 0 : "auto",
                          backgroundColor: colors.grey[100],
                          zIndex: col.pinned === "left" ? 2 : undefined,
                          userSelect: "none",
                          textTransform: "capitalize",
                          letterSpacing: "0.01em",
                          cursor:
                            resizingColumnKey === col.key
                              ? "col-resize"
                              : "default",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => {
                          e.preventDefault();
                          const dropIndex = index;
                          const dragIndex = Number.parseInt(
                            e.dataTransfer.getData("text/plain"),
                            10
                          );
                          if (dragIndex !== dropIndex) {
                            handleColumnReorder(dragIndex, dropIndex);
                          }
                          setDraggedColumnIndex(null);
                        }}
                      >
                        <Box
                          component="span"
                          title={`Click to sort by ${formatLabel(col.label)} or drag to reorder`}
                          sx={{
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            "&:hover": {
                              cursor:
                                col.pinned === "left" ? "default" : "grab",
                            },
                            "&:active": {
                              cursor:
                                col.pinned === "left" ? "default" : "grabbing",
                            },
                            "&:hover .sort-icon": { opacity: 1 },
                          }}
                          draggable={!resizingColumnKey && col.pinned !== "left"}
                          onDragStart={(e) => {
                            if (col.pinned === "left") {
                              e.preventDefault();
                              return;
                            }
                            e.dataTransfer.setData(
                              "text/plain",
                              index.toString()
                            );
                            setDraggedColumnIndex(index);
                          }}
                          onDragEnd={() => setDraggedColumnIndex(null)}
                          onClick={() => handleSort(col.key)}
                        >
                          {formatLabel(col.label)}
                          {col.pinned === "left" && (
                            <PushPinIcon
                              sx={{
                                ml: 0.5,
                                fontSize: 14,
                                color: "var(--ems-muted)",
                                opacity: 0.85,
                                transform: "rotate(-20deg)",
                              }}
                              titleAccess="Pinned column"
                            />
                          )}
                          {col.sortable && (
                            <Box
                              className="sort-icon"
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                fontSize: "14px",
                                opacity:
                                  sortConfig[0].key === col.key ? 0.6 : 0,
                                transition: "opacity 0.2s ease-in-out",
                                color: colors.primary,
                                "&:hover": { opacity: 0.8 },
                              }}
                            >
                              {sortConfig[0].key === col.key
                                ? sortConfig[0].direction === "asc"
                                  ? "↑"
                                  : "↓"
                                : "⇅"}
                            </Box>
                          )}
                        </Box>
                        <Box
                          sx={{
                            position: "absolute",
                            right: 0,
                            top: 0,
                            bottom: 0,
                            width: isSmallScreen ? "24px" : "16px",
                            cursor: "col-resize",
                            backgroundColor:
                              resizingColumnKey === col.key
                                ? colors.primary
                                : "transparent",
                            opacity:
                              resizingColumnKey === col.key
                                ? 0.5
                                : isSmallScreen
                                  ? 0.55
                                  : 0.2,
                            "&:hover": {
                              opacity: 1,
                              backgroundColor: colors.primary,
                            },
                            transition:
                              "opacity 0.2s ease-in-out, background-color 0.2s ease-in-out",
                            zIndex: 2,
                          }}
                          onMouseDown={(e) => handleMouseDown(e, col.key)}
                          onTouchStart={(e) => {
                            e.stopPropagation();
                            const touch = e.touches?.[0];
                            if (!touch) return;
                            beginColumnResize(col.key, touch.clientX);
                          }}
                        />
                      </TableCell>
                    ))}

                    {showActions && (
                      <TableCell
                        className="ems-actions-col"
                        sx={{
                          p: "8px 16px",
                          fontWeight: 650,
                          fontSize: "12px",
                          color: "var(--ems-faint)",
                          textAlign: visibleColumns.length === 0 ? "right" : "center",
                          backgroundColor: "var(--ems-surface-2)",
                          borderBottom: `1px solid ${colors.grey[300]}`,
                          position: "sticky",
                          right: 0,
                          zIndex: 4,
                          whiteSpace: "nowrap",
                          letterSpacing: "0.01em",
                        }}
                      >
                        Actions
                      </TableCell>
                    )}
                  </TableRow>
                </TableHead>

                {/* ---------- TABLE BODY ---------- */}
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell
                        colSpan={visibleColumns.length + 2}
                        sx={{ textAlign: "center", py: 3 }}
                      >
                        <CircularProgress
                          size={24}
                          sx={{ color: colors.primary }}
                        />
                        <Typography
                          variant="body2"
                          sx={{ mt: 1, ml: 2, color: colors.text.secondary }}
                        >
                          Loading data...
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedData?.map((row, rowIndex) => {
                      const isSelected = selectedRows.includes(row[mainKey]);
                      return (
                        <TableRow
                          key={row?.[mainKey] || rowIndex}
                          hover
                          selected={isSelected}
                          onClick={() => handleSelectRow(row[mainKey])} // ✅ click anywhere to select
                          sx={{
                            cursor: "pointer",
                            backgroundColor: isSelected
                              ? theme.palette.action.selected
                              : "inherit",
                            // Pinned column has subtle background, matches row only on hover/selection
                            "& .pinned-column, & .ems-actions-col": {
                              backgroundColor: `${colors.background} !important`,
                            },
                            "&:hover .pinned-column, &:hover .ems-actions-col": {
                              backgroundColor: `${theme.palette.action.hover} !important`,
                            },
                            "&.Mui-selected .pinned-column, &.Mui-selected .ems-actions-col": {
                              backgroundColor: `${theme.palette.action.selected} !important`,
                            },
                            "&.Mui-selected:hover .pinned-column, &.Mui-selected:hover .ems-actions-col": {
                              backgroundColor: `${theme.palette.action.hover} !important`,
                            },
                          }}
                        >
                          <TableCell sx={{ p: 1, textAlign: "center" }}>
                            <Checkbox
                              checked={isSelected}
                              onClick={(e) => e.stopPropagation()}
                              onChange={() => handleSelectRow(row[mainKey])}
                              sx={{
                                opacity:
                                  showRowCheckboxes || isSelected ? 1 : 0,
                                transition: "opacity 0.25s ease",
                              }}
                            />
                          </TableCell>

                          {/* ✅ Data cells */}
                          {visibleColumns.map((col) => (
                            <TableCell
                              key={col.key}
                              className={
                                col.pinned === "left" ? "pinned-column" : ""
                              }
                              sx={{
                                p: 2,
                                fontSize: "14px",
                                color: colors.text.primary,
                                position:
                                  col.pinned === "left" ? "sticky" : "relative",
                                left: col.pinned === "left" ? 0 : "auto",
                                backgroundColor:
                                  col.pinned === "left"
                                    ? colors.background
                                    : "inherit",
                                transition: "background-color 0.2s ease",
                                zIndex: col.pinned === "left" ? 1 : undefined,
                                borderBottom: `1px solid ${colors.grey[200]}`,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                ...getColumnWidthSx(col.width),
                              }}
                            >
                              {renderDataCellContent(row, col)}
                            </TableCell>
                          ))}

                          {/* ✅ Actions */}
                          {showActions && (
                            <TableCell
                              className="ems-actions-col"
                              sx={{
                                p: 2,
                                textAlign: "center",
                                borderBottom: `1px solid ${colors.grey[200]}`,
                                minWidth: 0,
                                overflow: "visible",
                                position: "sticky",
                                right: 0,
                                zIndex: 3,
                                backgroundColor: "var(--ems-surface)",
                              }}
                            >
                              {wrapActionCellWithActiveToggle(
                                row,
                                <>
                                  <Tooltip title="Show">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleShowData(row)}
                                      sx={actionBtnSx}
                                    >
                                      <Eye size={16} />{" "}
                                      {/* ← Replaced Edit with Eye */}
                                    </IconButton>
                                  </Tooltip>

                                  {canEditAction && (
                                    <Tooltip title="Edit">
                                      <IconButton
                                        size="small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleEdit(row);
                                        }}
                                        sx={actionBtnSx}
                                      >
                                        <Edit size={16} />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                  {canDeleteAction && (
                                    <UsersDeleteButton
                                      row={row}
                                      userData={userData}
                                      tableName={tableName}
                                      onDelete={handleDelete}
                                      sx={actionBtnSx}
                                      stopPropagation
                                    />
                                  )}
                                  {typeof additionalActions === "function" &&
                                    additionalActions(row)}
                                </>
                              )}
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            )
          ) : (
            <div className="ems-mcard-list ems-mcard-list--grid">
              {paginatedData?.map((row, index) => (
                <ListRecordCard
                  key={row?.[mainKey] ?? index}
                  row={row}
                  columns={visibleColumns}
                  getNestedValue={getNestedValue}
                  renderCell={renderDataCellContent}
                  tableName={tableName}
                  showNameAvatar={showNameAvatar}
                  onOpen={() => handleShowData(row)}
                  actions={renderCardActions(row)}
                />
              ))}
            </div>
          )}
        </div>
      )}

      <TablePagination
        paginationData={effectivePaginationData}
        theme={theme}
        isSmallScreen={isSmallScreen}
        colors={colors}
        paginatedDataLength={paginatedData.length}
        totalRecords={effectivePaginationData?.total ?? data.length}
        onPageChange={onPageChange}
      />

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            overflow: "hidden",
            boxShadow: "0 8px 32px rgba(47, 54, 69, 0.14)",
          },
        }}
      >
        <DialogTitle
          sx={{
            m: 0,
            px: 2.5,
            py: 2,
            backgroundColor: "#fff",
            color: "#0891B2",
            borderBottom: "1px solid #e8eaef",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              variant="h6"
              component="span"
              sx={{ fontWeight: 600, fontSize: "1.05rem" }}
            >
              Confirm Delete
            </Typography>
            <IconButton
              onClick={() => setDeleteDialogOpen(false)}
              sx={{ color: "#6b7280", p: 0.5 }}
              aria-label="Close"
            >
              <X size={20} />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent
          sx={{
            p: 3,
            backgroundColor: "#fff",
          }}
        >
          <Typography
            variant="body1"
            sx={{ color: "#4b5563", lineHeight: 1.6 }}
          >
            {deleteRestoreOption
              ? "Are you sure you want to delete this exit record? The employee will be moved back to the employee list with status Working and stage Confirm."
              : "Are you sure you want to permanently delete this item? This action cannot be undone. This will also delete all the related data."}
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            p: 3,
            pt: 0,
            backgroundColor: "#fff",
            justifyContent: "flex-end",
            gap: 1.5,
            flexWrap: "wrap",
          }}
        >
          <Button
            variant="outlined"
            onClick={() => setDeleteDialogOpen(false)}
            sx={{
              color: "#6b7280",
              borderColor: "#d1d5db",
              minWidth: 100,
              fontWeight: 600,
              fontSize: "0.8125rem",
              borderRadius: "9px",
              textTransform: "none",
              "&:hover": {
                borderColor: "#9ca3af",
                backgroundColor: "#f9fafb",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => handleDeleteConfirm(!!deleteRestoreOption)}
            sx={{
              backgroundColor: "#0891B2",
              color: "#fff",
              minWidth: 100,
              fontWeight: 600,
              fontSize: "0.8125rem",
              borderRadius: "9px",
              textTransform: "none",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "#077d9c",
                boxShadow: "none",
              },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {snackbar.open && (
        <Box
          sx={{
            position: "fixed",
            bottom: 24,
            right: 24,
            zIndex: 9999,
            maxWidth: 400,
          }}
        >
          <Paper
            elevation={6}
            sx={{
              p: 2,
              backgroundColor:
                snackbar.severity === "error"
                  ? colors.error
                  : snackbar.severity === "warning"
                    ? colors.warning
                    : snackbar.severity === "info"
                      ? colors.info
                      : colors.success,
              color: "white",
              borderRadius: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Typography variant="body2" sx={{ color: "white" }}>
              {snackbar.message}
            </Typography>
            <IconButton
              size="small"
              onClick={() => setSnackbar({ ...snackbar, open: false })}
              sx={{ color: "white", p: 0.5 }}
            >
              <X size={16} />
            </IconButton>
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default Customisetable;
